import { BrandCard, Loader } from "@shltelemedicine/shl-ui-lib";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import OpenReport from "../../components/Shared/OpenReport/OpenReport";
import ViewEcg from "../../components/ViewEcg/ViewEcg";
import { Constants } from "../../constants";
import ecgsService, { GetPdfByTokenParams } from "../../services/ecgs.service";
import LocalStorageUtil from "../../utils/localStorage";
import styles from "./ViewEcgPage.module.css";

export const ViewEcgPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [searchParams] = useSearchParams();

  const pdfToken = searchParams.get(Constants.ECG_VIEW_PDF_TOKEN_KEY);
  const ssoToken = searchParams.get(Constants.ECG_VIEW_SSO_TOKEN_KEY);

  const [pdfFile, setPdfFile] = useState<Blob>();

  const init = useCallback(async () => {
    if (!pdfToken) {
      return;
    }

    try {
      setLoading(true);

      const authToken = LocalStorageUtil.get(Constants.JWT_KEY);

      if (!authToken && !ssoToken) {
        return;
      }

      let getPdfParams: GetPdfByTokenParams = {
        pdfToken,
      };

      if (authToken) {
        getPdfParams = { ...getPdfParams, authToken };
      } else if (ssoToken) {
        getPdfParams = { ...getPdfParams, ssoToken };
      }

      // If we got here, token (auth / sso) exists, try getting PDF
      const { success, data, errorCode } = await ecgsService.downloadPdfByToken(
        getPdfParams
      );

      if (success && data) {
        setPdfFile(data);
        return;
      }

      // If we got here, Unable to create PDF
      if (authToken && errorCode === "401") {
        // Unauthorized - clean invalid token
        LocalStorageUtil.remove([Constants.JWT_KEY]);
      }

      if (ssoToken) {
        // For SSO flow, we can't show the "View ECG form",
        // so we just display an error
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [pdfToken, ssoToken]);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <BrandCard className={`${styles.wrapper} px-5`} contentClassName="mb-4">
      {loading && <Loader />}

      {pdfFile ? (
        <OpenReport
          file={pdfFile}
          titleStringId="VIEW_ECG.REPORT_CREATED"
          linkStringId="VIEW_ECG.VIEW_REPORT"
        />
      ) : (
        <>
          {(!pdfToken || error) && (
            <h5 className="m-0 text-center">
              <FormattedMessage id={"VIEW_ECG.UNABLE_TO_VIEW"} />
            </h5>
          )}
          {pdfToken && !ssoToken && !loading && !error && (
            <ViewEcg pdfToken={pdfToken} />
          )}
        </>
      )}
    </BrandCard>
  );
};
